import { useId } from 'react';
import type { ReactNode } from 'react';
import * as RadixCheckbox from '@radix-ui/react-checkbox';
import * as Popover from '@radix-ui/react-popover';
import { Check, HelpCircle } from 'lucide-react';

import styles from './Checkbox.module.css';

interface Props {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  label: string;
  /** Krótkie zdanie pod etykietą — to, co człowiek musi wiedzieć, zanim kliknie. */
  hint?: string;
  /**
   * Pełne wyjaśnienie za znakiem zapytania.
   *
   * ⭐ **Popover, nie natywny tooltip** (słowo usera 2026-09-04: „radix popover jest okej").
   * Natywny nie działa na dotyku, znika przy próbie przeczytania i nie mieści akapitu —
   * a tutaj wyjaśnienie ma być obszerne.
   */
  explanation?: ReactNode;
  /** Etykieta przycisku „?" — czytnik ekranu musi wiedzieć, czego dotyczy. */
  explanationLabel?: string;
  disabled?: boolean;
}

/**
 * Przełącznik dwustanowy z opcjonalnym wyjaśnieniem.
 *
 * ⭐ Radix, nie własne: to jest kontrolka z prawdziwym stanem, ogniskiem i obsługą klawiatury —
 * dokładnie ta kategoria, o której spec 12B.3 mówi „Radix na trudne, własne na proste".
 *
 * ⚠ **Wyjaśnienie stoi POZA etykietą**, a nie w środku niej: etykieta obejmująca kontrolkę
 * wciąga do nazwy dostępnej cały swój tekst, więc podpowiedź w środku dałaby pole o nazwie
 * „Nadpisuj nazwy Kiedy włączone, import…" (zmierzone 2026-08-22 przy panelu operatora).
 */
export function Checkbox({
  checked,
  onCheckedChange,
  label,
  hint,
  explanation,
  explanationLabel,
  disabled = false,
}: Props) {
  const id = useId();
  const hintId = `${id}-hint`;

  return (
    <div className={styles['row']}>
      <RadixCheckbox.Root
        id={id}
        className={styles['box']}
        checked={checked}
        disabled={disabled}
        onCheckedChange={(value) => onCheckedChange(value === true)}
        {...(hint ? { 'aria-describedby': hintId } : {})}
      >
        <RadixCheckbox.Indicator className={styles['indicator']}>
          <Check size={14} aria-hidden="true" />
        </RadixCheckbox.Indicator>
      </RadixCheckbox.Root>

      <div className={styles['text']}>
        <span className={styles['labelRow']}>
          <label className={styles['label']} htmlFor={id}>
            {label}
          </label>

          {explanation ? (
            <Popover.Root>
              <Popover.Trigger asChild>
                <button
                  type="button"
                  className={styles['help']}
                  aria-label={explanationLabel ?? label}
                >
                  <HelpCircle size={15} aria-hidden="true" />
                </button>
              </Popover.Trigger>

              <Popover.Portal>
                <Popover.Content className={styles['popover']} sideOffset={6} collisionPadding={12}>
                  {explanation}
                  <Popover.Arrow className={styles['arrow']} />
                </Popover.Content>
              </Popover.Portal>
            </Popover.Root>
          ) : null}
        </span>

        {hint ? (
          <span className={styles['hint']} id={hintId}>
            {hint}
          </span>
        ) : null}
      </div>
    </div>
  );
}
